import React from 'react';
import styled from 'styled-components';

const PricingContainer = styled.section`
  width: 100%;
  padding: ${props => props.theme.spacing.mega} 0;
  background: ${props => props.theme.colors.background.primary};
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.large};
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xxlarge};
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.hero};
  margin-bottom: ${props => props.theme.spacing.medium};

  background: linear-gradient(
    135deg,
    ${props => props.theme.colors.primary} 0%,
    ${props => props.theme.colors.neon.cyan} 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: ${props => props.theme.fontSizes.xxlarge};
  }
`;

const SectionSubtitle = styled.p`
  font-size: ${props => props.theme.fontSizes.large};
  color: ${props => props.theme.colors.text.secondary};
  max-width: 700px;
  margin: 0 auto;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${props => props.theme.spacing.xlarge};

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    grid-template-columns: 1fr;
    max-width: 500px;
    margin: 0 auto;
  }
`;

const PricingCard = styled.div<{ $featured?: boolean }>`
  background: ${props => props.theme.colors.background.card};
  border: 2px solid ${props => props.$featured
    ? props.theme.colors.primary
    : props.theme.colors.border.secondary};
  border-radius: ${props => props.theme.borderRadius.xlarge};
  padding: ${props => props.theme.spacing.xxlarge};
  text-align: center;
  transition: all ${props => props.theme.transitions.medium};
  position: relative;
  overflow: hidden;
  transform: ${props => props.$featured ? 'scale(1.05)' : 'scale(1)'};

  ${props => props.$featured && `
    box-shadow: ${props.theme.shadows.glowStrong};
    z-index: 2;
  `}

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: linear-gradient(
      90deg,
      ${props => props.theme.colors.primary} 0%,
      ${props => props.theme.colors.neon.cyan} 50%,
      ${props => props.theme.colors.neon.purple} 100%
    );
    opacity: ${props => props.$featured ? '1' : '0'};
    transition: opacity ${props => props.theme.transitions.medium};
  }

  &:hover {
    transform: scale(1.05);
    border-color: ${props => props.theme.colors.primary};
    box-shadow: ${props => props.theme.shadows.glow};

    &::before {
      opacity: 1;
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    transform: scale(1) !important;
  }
`;

const Badge = styled.div`
  display: inline-block;
  padding: ${props => props.theme.spacing.small} ${props => props.theme.spacing.medium};
  background: linear-gradient(
    135deg,
    ${props => props.theme.colors.primary} 0%,
    ${props => props.theme.colors.primaryDark} 100%
  );
  color: ${props => props.theme.colors.background.primary};
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: ${props => props.theme.fontSizes.small};
  font-weight: ${props => props.theme.fontWeights.bold};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: ${props => props.theme.spacing.medium};
`;

const PlanName = styled.h3`
  font-size: ${props => props.theme.fontSizes.xxlarge};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: ${props => props.theme.spacing.medium};
  font-weight: ${props => props.theme.fontWeights.bold};
  text-transform: uppercase;
`;

const PriceWrapper = styled.div`
  margin-bottom: ${props => props.theme.spacing.xlarge};
`;

const Price = styled.div`
  font-size: 3.5rem;
  font-weight: ${props => props.theme.fontWeights.extrabold};
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.xsmall};
  line-height: 1;
  font-family: ${props => props.theme.fonts.heading};

  span {
    font-size: ${props => props.theme.fontSizes.xlarge};
    color: ${props => props.theme.colors.text.secondary};
  }
`;

const BillingCycle = styled.p`
  font-size: ${props => props.theme.fontSizes.medium};
  color: ${props => props.theme.colors.text.secondary};
`;

const FeaturesList = styled.ul`
  list-style: none;
  margin-bottom: ${props => props.theme.spacing.xlarge};
  text-align: left;
`;

const FeatureItem = styled.li<{ $available: boolean }>`
  padding: ${props => props.theme.spacing.small} 0;
  font-size: ${props => props.theme.fontSizes.medium};
  color: ${props => props.$available
    ? props.theme.colors.text.primary
    : props.theme.colors.text.disabled};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.small};

  &::before {
    content: '${props => props.$available ? '✓' : '✗'}';
    color: ${props => props.$available
      ? props.theme.colors.primary
      : props.theme.colors.text.disabled};
    font-weight: ${props => props.theme.fontWeights.bold};
    font-size: ${props => props.theme.fontSizes.large};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.medium};
`;

const OSIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const CTAButton = styled.a<{ $variant: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.medium};
  width: 100%;
  padding: ${props => props.theme.spacing.medium};
  font-size: ${props => props.theme.fontSizes.medium};
  font-weight: ${props => props.theme.fontWeights.semibold};
  text-transform: uppercase;
  letter-spacing: 1px;
  border-radius: ${props => props.theme.borderRadius.medium};
  transition: all ${props => props.theme.transitions.medium};
  cursor: pointer;
  text-decoration: none;

  ${props => props.$variant === 'primary' ? `
    background: linear-gradient(135deg, ${props.theme.colors.primary} 0%, ${props.theme.colors.primaryDark} 100%);
    color: ${props.theme.colors.background.primary};
    border: none;
    box-shadow: ${props.theme.shadows.glow};

    &:hover {
      box-shadow: ${props.theme.shadows.glowStrong};
      transform: translateY(-2px);
    }
  ` : `
    background: transparent;
    border: 2px solid ${props.theme.colors.border.primary};
    color: ${props.theme.colors.primary};

    &:hover {
      background: ${props.theme.colors.primary};
      color: ${props.theme.colors.background.primary};
      border-color: ${props.theme.colors.primary};
    }
  `}
`;

interface PricingPlan {
  name: string;
  price: string;
  billingCycle: string;
  badge?: string;
  featured?: boolean;
  features: Array<{ text: string; available: boolean }>;
  ctaText: string;
  ctaLink: string;
}

const PricingSection: React.FC = () => {
  const pricingPlans: PricingPlan[] = [
    {
      name: 'Explore',
      price: 'Free',
      billingCycle: 'Forever',
      features: [
        { text: 'Prompt Editor - Limited', available: true },
        { text: 'Parameters - Limited', available: true },
        { text: 'Repository - Limited', available: true },
        { text: 'IntelliPaste', available: false },
        { text: 'Advanced Scripts', available: false },
        { text: 'Priority Support', available: false },
      ],
      ctaText: 'Download Trial',
      ctaLink: '#download',
    },
    {
      name: 'Creative Pro',
      price: '$199',
      billingCycle: 'One-time payment',
      badge: 'Most Popular',
      featured: true,
      features: [
        { text: 'Unlimited Prompt Editor', available: true },
        { text: 'Full Parameters Catalog', available: true },
        { text: 'Unlimited Repository', available: true },
        { text: 'IntelliPaste Function', available: true },
        { text: 'Advanced Script Editor', available: true },
        { text: 'Priority Support', available: true },
      ],
      ctaText: 'Buy Now',
      ctaLink: '#buy',
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      billingCycle: 'Contact for pricing',
      badge: 'Coming Soon',
      features: [
        { text: 'Everything in Creative Pro', available: true },
        { text: 'Team Collaboration', available: true },
        { text: 'API Access', available: true },
        { text: 'Custom Integrations', available: true },
        { text: 'Dedicated Support', available: true },
        { text: 'Training & Onboarding', available: true },
      ],
      ctaText: 'Contact Sales',
      ctaLink: '#contact',
    },
  ];

  return (
    <PricingContainer id="versions">
      <Container>
        <SectionHeader>
          <SectionTitle>Choose Your Version</SectionTitle>
          <SectionSubtitle>
            Select the perfect plan for your creative workflow
          </SectionSubtitle>
        </SectionHeader>

        <PricingGrid>
          {pricingPlans.map((plan, index) => (
            <PricingCard key={index} $featured={plan.featured}>
              {plan.badge && <Badge>{plan.badge}</Badge>}

              <PlanName>{plan.name}</PlanName>

              <PriceWrapper>
                <Price>
                  {plan.price}
                  {plan.price.includes('$') && <span> USD</span>}
                </Price>
                <BillingCycle>{plan.billingCycle}</BillingCycle>
              </PriceWrapper>

              <FeaturesList>
                {plan.features.map((feature, idx) => (
                  <FeatureItem key={idx} $available={feature.available}>
                    {feature.text}
                  </FeatureItem>
                ))}
              </FeaturesList>

              {plan.name === 'Enterprise' ? (
                <CTAButton
                  href={plan.ctaLink}
                  $variant={plan.featured ? 'primary' : 'secondary'}
                >
                  {plan.ctaText}
                </CTAButton>
              ) : (
                <ButtonGroup>
                  <CTAButton
                    href={plan.name === 'Explore'
                      ? 'https://dynappmos.com:8844/neuralpromptweb/registrydownload?bintarget=33e7f1d8-fa6a-4fc4-9189-e341b73bc895'
                      : plan.ctaLink}
                    $variant={plan.featured ? 'primary' : 'secondary'}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <OSIcon src="/assets/images/apple-logo.svg" alt="Apple" />
                    Desktop Download
                  </CTAButton>
                  <CTAButton
                    href={plan.name === 'Explore'
                      ? 'https://dynappmos.com:8844/neuralpromptweb/registrydownload?bintarget=853a02bf-794b-4343-b22c-1cccebd35286'
                      : plan.ctaLink}
                    $variant={plan.featured ? 'primary' : 'secondary'}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <OSIcon src="/assets/images/windows-logo.svg" alt="Windows" />
                    Desktop Download
                  </CTAButton>
                </ButtonGroup>
              )}
            </PricingCard>
          ))}
        </PricingGrid>
      </Container>
    </PricingContainer>
  );
};

export default PricingSection;
